import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Heading,
  Textarea,
  FileUpload,
  Icon,
} from '@chakra-ui/react';
import { LuUpload } from 'react-icons/lu';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { FormErrorDisplay, RootErrorDisplay } from '@/components/ui/FormErrorDisplay';
import { useGetConditionLookupsQuery } from '@entities/conditions/api';
import Select from '@/components/ui/Select';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { 
  ConditionResourceLookup,
  useCreateConditionResourceLookupMutation,
  useUpdateConditionResourceLookupMutation,
  useGetConditionResourceLookupQuery,
} from '../api';
import { useUploadFileMutation } from '@entities/files';
import { RESOURCE_TYPE_OPTIONS } from '../constants';
import { useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { LOOKUP_STATUSES, LOOKUP_STATUS_OPTIONS } from '@/utils/lookupStatus';

// Schema definition
const conditionResourceSchema = yup.object({
  condition_lookup_id: yup
    .number()
    .required('Condition is required')
    .min(1, 'Please select a valid condition'),
  title: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .max(255, 'Title must be less than 255 characters'),
  description: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .max(300, 'Description must be less than 300 characters'),
  url: yup
    .string()
    .when('upload_method', {
      is: 'url',
      then: (schema) => schema.required('URL is required').url('Please enter a valid URL'),
      otherwise: (schema) => schema.transform((value) => value === '' ? undefined : value).url('Please enter a valid URL')
    }),
  resource_type: yup
    .string()
    .transform((value) => value === '' ? undefined : value),
  status: yup
    .string()
    .optional()
    .oneOf([...LOOKUP_STATUSES, ''], 'Invalid status'),
  is_valid: yup
    .boolean()
    .nullable(),
  upload_method: yup
    .string()
    .oneOf(['url', 'file'], 'Please select a valid upload method'),
});

type ConditionResourceFormData = yup.InferType<typeof conditionResourceSchema>;

interface ConditionResourceCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of condition resource to fetch (for hash modal edit mode)
  onSuccess?: () => void;
  resourceType: 'resource' | 'recommendation' | 'prescription'; // Make this required instead of defaultResourceType
}

const ConditionResourceCreateEditModal: React.FC<ConditionResourceCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
  resourceType, // Use resourceType instead of defaultResourceType
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { data: editingEntity } = useGetConditionResourceLookupQuery(entityId!, {
    skip: !entityId,
  });

  const isEditing = Boolean(entityId && editingEntity);
  const [createResource, { isLoading: isCreating }] = useCreateConditionResourceLookupMutation();
  const [updateResource, { isLoading: isUpdating }] = useUpdateConditionResourceLookupMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const { data: conditionsResponse } = useGetConditionLookupsQuery({
    per_page: 10000, // Get all conditions for the dropdown
  });
  const conditions = conditionsResponse?.data || [];

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedUrl, setUploadedUrl] = useState<string>('');
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  // Handle file upload when file is selected
  const handleFileUpload = async (files: File[]) => {
    setUploadedFiles(files);
    
    if (files.length > 0) {
      try {
        const formData = new FormData();
        formData.append('file', files[0]);
        
        const uploadResult = await uploadFile(formData).unwrap();
        if (uploadResult.status && uploadResult.url) {
          setUploadedUrl(uploadResult.url);
          // Auto-populate the URL field
          setValue('url', uploadResult.url);
        }
      } catch (error) {
        console.error('File upload failed:', error);
        // Clear the uploaded files on error
        setUploadedFiles([]);
        setUploadedUrl('');
        // Show error to user (you could use a toast notification here)
        setError('url', { 
          type: 'manual', 
          message: 'File upload failed. Please try again.' 
        });
      }
    }
  };

  // Determine if we should show description field (only for videos)
  const showDescriptionField = resourceType === 'recommendation';
  // Show resource_type field only for resources
  const showResourceTypeField = resourceType === 'resource';

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isValid },
  } = useForm<ConditionResourceFormData>({
    resolver: yupResolver(conditionResourceSchema) as any,
    mode: 'onChange',
    defaultValues: {
      condition_lookup_id: undefined,
      title: '',
      description: '',
      url: '',
      resource_type: '',
      status: 'draft',
      is_valid: null,
      upload_method: showResourceTypeField ? 'url' : undefined, // Default to URL for resources, undefined for others
    },
  });

  const { withErrorHandling } = useFormErrorHandler(setError, clearErrors);

  // Watch upload method for conditional rendering
  const uploadMethod = useWatch({ control, name: 'upload_method' });

  // Handle clearing the uploaded file
  const handleClearUpload = useCallback((e?: React.MouseEvent) => {
    // Prevent event bubbling to avoid triggering file upload
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setUploadedFiles([]);
    setUploadedUrl('');
    setValue('url', '');
    clearErrors('url');
    // Reset file input key to allow re-selection of same file
    setFileInputKey(prev => prev + 1);
  }, [setValue, clearErrors]);

  // Handle modal close with form reset
  const handleClose = useCallback(() => {
    reset({
      condition_lookup_id: undefined,
      title: '',
      description: '',
      url: '',
      resource_type: '',
      status: 'draft',
      is_valid: null,
      upload_method: showResourceTypeField ? 'url' : undefined,
    });
    handleClearUpload();
    setFileInputKey(prev => prev + 1);
    onClose();
  }, [reset, showResourceTypeField, handleClearUpload, onClose]);

  // Reset form when modal opens or editingEntity changes
  useEffect(() => {
    if (isOpen && editingEntity) {
      reset({
        condition_lookup_id: editingEntity.condition_lookup_id || undefined,
        title: editingEntity.title || '',
        description: editingEntity.description || '',
        url: editingEntity.url || '',
        resource_type: editingEntity.resource_type || '', // Use resource_type for the Content Type field
        status: editingEntity.status || 'draft',
        is_valid: editingEntity.is_valid ?? null,
        upload_method: 'url', // Existing resources always have URL
      });
      // Don't clear uploads when editing - preserve existing URL
      setUploadedFiles([]);
      setUploadedUrl('');
    } else if (isOpen && !entityId) {
      // New mode - reset to empty values
      reset({
        condition_lookup_id: undefined,
        title: '',
        description: '',
        url: '',
        resource_type: '',
        status: 'draft',
        is_valid: null,
        upload_method: showResourceTypeField ? 'url' : undefined,
      });
      handleClearUpload();
    }
  }, [isOpen, editingEntity, entityId, reset, showResourceTypeField, handleClearUpload]);

  const onSubmit = async (data: ConditionResourceFormData) => {
    await withErrorHandling(async () => {
      // Validate that if file upload method is selected, we have an uploaded URL
      if (data.upload_method === 'file' && !uploadedUrl) {
        throw new Error('Please select a file to upload');
      }

      // Use the data.url which should be populated by file upload or manual entry
      const finalUrl = data.url || '';

      // Map form data to API format
      const apiData = {
        condition_lookup_id: data.condition_lookup_id,
        title: data.title,
        description: data.description,
        url: finalUrl,
        type: resourceType,
        resource_type: data.resource_type || '',
        status: data.status as any,
        is_valid: data.is_valid,
      };

      if (isEditing && editingEntity) {
        await updateResource({
          id: editingEntity.id,
          data: apiData,
        }).unwrap();
      } else {
        await createResource(apiData).unwrap();
      }

      onSuccess?.();
      handleClose();
    });
  };

  const contentTypes = RESOURCE_TYPE_OPTIONS;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>
          {isEditing ? 'Edit Condition Resource' : 'Create New Condition Resource'}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <form onSubmit={handleSubmit(onSubmit as any)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DrawerBody>
          <VStack gap={4} align="stretch">
            <RootErrorDisplay />

            {/* Condition Selection */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Condition <Text as="span" color="red.500">*</Text>
              </Text>
              <Controller
                name="condition_lookup_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value?.toString() || ''}
                    onChange={(value) => field.onChange(value ? parseInt(value) : undefined)}
                    options={conditions.map(condition => ({
                      value: condition.id.toString(),
                      label: condition.name,
                    }))}
                    placeholder="Select a condition"
                  />
                )}
              />
              <FormErrorDisplay error={errors.condition_lookup_id} />
            </Box>

            {/* Title */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Title
              </Text>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter resource title (optional)"
                  />
                )}
              />
              <FormErrorDisplay error={errors.title} />
            </Box>

            {/* Description - Only show for videos */}
            {showDescriptionField && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Description
                </Text>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder="Enter resource description (optional, max 300 characters)"
                      rows={4}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.description} />
              </Box>
            )}

            {/* Upload Method - Only show for resources */}
            {showResourceTypeField && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Upload Method <Text as="span" color="red.500">*</Text>
                </Text>
                <Controller
                  name="upload_method"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Select upload method"
                      options={[
                        { value: 'url', label: 'URL' },
                        { value: 'file', label: 'File Upload' },
                      ]}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.upload_method} />
              </Box>
            )}

            {/* URL - Show for all types, but conditional requirement for resources */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                URL {showResourceTypeField && uploadMethod === 'url' ? (
                  <Text as="span" color="red.500">*</Text>
                ) : (
                  !showResourceTypeField && <Text as="span" color="red.500">*</Text>
                )}
              </Text>
              <Controller
                name="url"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="url"
                    placeholder="https://example.com/resource"
                    disabled={showResourceTypeField && uploadMethod === 'file'}
                  />
                )}
              />
              <FormErrorDisplay error={errors.url} />
            </Box>

            {/* File Upload - Only show for resources when file method is selected */}
            {showResourceTypeField && uploadMethod === 'file' && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Upload File <Text as="span" color="red.500">*</Text>
                </Text>
                <FileUpload.Root
                  key={fileInputKey}
                  maxFiles={1}
                  onFileAccept={(details) => handleFileUpload(details.files)}
                  accept={{
                    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  }}
                  width="full"
                >
                  <FileUpload.HiddenInput />
                  <FileUpload.Dropzone width="full" minH="120px" display="flex" alignItems="center" justifyContent="center" position="relative">
                    {uploadedFiles.length > 0 ? (
                      // Show file info when file is uploaded
                      <HStack justify="space-between" align="center" width="full" p={4}>
                        <HStack gap={3}>
                          <Icon size="md" color="green.500">
                            <LuUpload />
                          </Icon>
                          <Box>
                            <Text fontSize="sm" fontWeight="medium">
                              {uploadedFiles[0].name}
                            </Text>
                            <Text fontSize="xs" color="fg.muted">
                              {(uploadedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                              {uploadedUrl && ' • Uploaded successfully!'}
                            </Text>
                          </Box>
                        </HStack>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={(e) => handleClearUpload(e)}
                        >
                          Clear
                        </Button>
                      </HStack>
                    ) : (
                      // Show upload prompt when no file
                      <>
                        <Icon size="md" color="fg.muted">
                          <LuUpload />
                        </Icon>
                        <FileUpload.DropzoneContent>
                          <Box>
                            {isUploading ? 'Uploading...' : 'Drag and drop file here'}
                          </Box>
                          <Box color="fg.muted">
                            Images, PDF, DOC up to 5MB
                          </Box>
                        </FileUpload.DropzoneContent>
                      </>
                    )}
                  </FileUpload.Dropzone>
                </FileUpload.Root>
              </Box>
            )}

            {/* Content Type - Only show for resources */}
            {showResourceTypeField && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Content Type <Text as="span" color="red.500">*</Text>
                </Text>
                <Controller
                  name="resource_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select content type"
                      options={contentTypes}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.resource_type} />
              </Box>
            )}

            {/* Status */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Status
              </Text>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || ''}
                    onChange={(value) => field.onChange(value)}
                    options={LOOKUP_STATUS_OPTIONS}
                    placeholder="Select status"
                  />
                )}
              />
              <FormErrorDisplay error={errors.status} />
            </Box>

            {/* Validation Status */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Validation Status
              </Text>
              <Controller
                name="is_valid"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value === null ? "unknown" : field.value === true ? "valid" : "invalid"}
                    onChange={(value) => {
                      if (value === "valid") field.onChange(true);
                      else if (value === "invalid") field.onChange(false);
                      else field.onChange(null);
                    }}
                    options={[
                      { value: "unknown", label: "Unknown" },
                      { value: "valid", label: "Valid" },
                      { value: "invalid", label: "Invalid" },
                    ]}
                    placeholder="Select validation status..."
                  />
                )}
              />
              <FormErrorDisplay error={errors.is_valid} />
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack gap={3}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isCreating || isUpdating || isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              disabled={!isValid || isCreating || isUpdating || isUploading}
              loading={isCreating || isUpdating || isUploading}
            >
              {isEditing ? 'Update Resource' : 'Create Resource'}
            </Button>
          </HStack>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const ConditionResourceCreateModal = withHashDrawer(ConditionResourceCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const ConditionResourceEditModal = withHashDrawer(ConditionResourceCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default ConditionResourceCreateModal; 
